import { PlanTimeframe } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'

import { dateToCivilDate } from '~/models/civilDate'
import { now as serverNow } from '~/models/clock'
import { startOfTodayJst, targetDateJstOffsetDays } from '~/models/dateTime'

/**
 * `today` の N 日後の 0:00 カレンダー日 `Date` を返す純関数。
 *
 * `today` を省略した場合は **JST の「今日」** (`startOfTodayJst()`) を基点にする
 * (PR #6542 レビュー指摘: 旧実装は基点を `serverNow()` の**端末ローカル壁時計**で丸めており、同一画面の
 * 他の JST 判定 (`timeframeForTargetDate` 等) と軸が混在していた。非 JST 端末で
 * JST 深夜前後に暦日がズレ、一覧の対象日初期値が 1 日誤る
 * 不具合があった)。`today` を明示指定した場合は、そのカレンダー日からの単純な
 * 日数加算になる (= JST 化は「基点の取得」だけが対象で、暦日の read/write は
 * 生成と対称にローカル getter/setter のまま)。
 */
export function dateAfterDays(
  days: number,
  today: Date = startOfTodayJst(),
): Date {
  const d = new Date(today)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + days)
  return d
}

/**
 * 対象日 → OCCTO 提出区分 (PlanTimeframe) の内部導出 (AC-PLC-043)。
 *
 * 許容範囲は **今日以降の任意の未来日**。「当日」を含める判断は
 * `docs/specs/plan-submit/plan-common.md` の issue #6816 Discussion 決定行で B 案
 * (当日を含める) が採用された。AC-PLC-039 は断面ラベル統合、AC-PLC-040 は初期値と
 * 提出区分の内部判定を定めるもので、対象日の下限自体は規定しない (むしろ AC-PLC-040 ※ は
 * 「対象日の入力自体に技術的な範囲制約は設けない」と書いており、下記の過去日拒否は
 * この ※ と食い違ったままの drift。範囲制約の要否は #6816 でスコープ外につき据え置き、
 * spec 側の整合は別途 = PdM 確認 issue #7175):
 * - 対象日=今日 (offset 0) → TODAY (当日計画)
 * - 対象日=明日 (offset 1) → TOMORROW (翌日計画)
 * - 対象日=明後日以降 (offset >= 2) → DAY_AFTER_TOMORROW (翌々日計画)
 * 過去日 (offset < 0) のみ `undefined` を返す total 関数で、戻り値そのものが
 * 「作成可能か」の判定を兼ねる (呼び出し側は `undefined` を作成不可の合図として扱う)。
 * 過去日は GC ロックで全コマ編集不可 (`mock-app/docs/domain-knowledge.md` §3 ゲートクローズ
 * の編集ロック規則表) のため作成不可のまま
 * (spec #6816 の Discussion 決定行「対象日自体の技術的な範囲制約〈過去日・明々後日以降の
 * 作成可否〉は本判断のスコープ外」により据え置き)。
 * なお当日も最終 GC (JST 22:30、最終コマ開始の 1 時間前) を過ぎると全 48 コマが GC 済みで
 * 過去日と同じ状態になるが、その場合も offset 0 = TODAY として受理する。過去日拒否との
 * この非対称は意図的で、対象日の範囲制約 (過去日・GC 全通過後の当日) は #6816 でスコープ外。
 *
 * offset>=2 を一律 DAY_AFTER_TOMORROW に clamp する根拠: PlanTimeframe は OCCTO フォーマット
 * ではなく **DB の business key** (版管理・最新 Accepted 計画の優先順位・workflow section 対応に使う)。
 * XML 取込経路の backend `deriveImportTimeframe`
 * (`go/internal/backend/application/usecase/plansubmit/import_assembler_demand_procurement.go`)
 * は offset<=0 を Today、offset>=2 を一律 DayAfterTomorrow に clamp している。
 * 作成ダイアログもこれに合わせないと、同一対象日が作成経路 (ダイアログ / XML 取込) ごとに
 * 異なる timeframe = 異なる business key になり版管理・最新計画選択が分裂する (PR #6820 レビュー指摘)。
 * OCCTO 提出時は Today/Tomorrow/DayAfterTomorrow すべて同一の「翌日計画」フォーマット (0250 系) で
 * 提出され差は提出タイミングのみ (`docs/domain-knowledge.md` L3109-3122、backend の OcctoTimeframe も同旨)
 * のため、DAY_AFTER_TOMORROW でも「翌日と同様のフォーマットで作成する」前提を満たす。
 *
 * 基準日は **JST 壁時計**で取得する。
 * `dateAfterDays` も既定で JST 基点だが、本関数は「絶対時刻 `now` → JST 暦日」の
 * 比較 (`targetDateJstOffsetDays`) を直接使う — 対象日は「カレンダー日 `Date`」
 * (ローカル getter で暦日を表現) として渡されるため、`dateToCivilDate`
 * で暦日を取り出してから JST の「今日」と比較する。
 */
export function timeframeForTargetDate(
  targetDate: Date,
  today: Date = serverNow(),
): PlanTimeframe | undefined {
  const offsetDays = targetDateJstOffsetDays(dateToCivilDate(targetDate), today)
  if (offsetDays === undefined) return undefined
  if (offsetDays === 0) return PlanTimeframe.TODAY
  if (offsetDays === 1) return PlanTimeframe.TOMORROW
  if (offsetDays >= 2) return PlanTimeframe.DAY_AFTER_TOMORROW
  return undefined
}
